import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Gallery", path: "/gallery" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    // Fixed: Added px-4 so it doesn't touch screen edges on mobile
    <nav className="fixed top-0 left-0 w-full z-50 flex justify-center items-center transition-all duration-500 py-6 px-4">
      <motion.div
        animate={{
          width: isScrolled ? "90%" : "100%",
          maxWidth: isScrolled ? "800px" : "100%",
          backgroundColor: isScrolled
            ? "rgba(255, 253, 245, 0.85)"
            : "rgba(255, 253, 245, 0)",
          borderRadius: isScrolled ? "50px" : "0px",
          padding: isScrolled ? "12px 24px" : "0px 40px",
          backdropFilter: isScrolled ? "blur(12px)" : "blur(0px)",
          boxShadow: isScrolled
            ? "0 10px 30px -10px rgba(180, 150, 100, 0.15)"
            : "none",
          marginTop: isScrolled ? "20px" : "0px",
        }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="flex items-center justify-between   w-full md:min-w-[800px] relative"
      >
        {/* LOGO AREA */}
        <Link to="/" className="z-[70]">
          <h1 className="font-serif text-2xl md:text-3xl text-dreamy-earth tracking-tighter italic cursor-pointer">
            Dusk Theory{" "}
            <span className="text-2xl text-dreamy-gold not-italic">by</span>
            <span className="text-dreamy-gold not-italic font-light text-2xl">
              {" "}
              Hasanka
            </span>
          </h1>
        </Link>

        {/* DESKTOP NAV LINKS */}
        <div className="hidden md:flex items-center gap-8 font-sans text-[11px] tracking-[0.2em] uppercase">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative py-1 transition-colors duration-300 ${
                  isActive
                    ? "text-dreamy-earth"
                    : "text-dreamy-earth/50 hover:text-dreamy-gold"
                }`}
              >
                {link.name}
                {isActive && (
                  <motion.div
                    layoutId="navUnderline"
                    className="absolute bottom-0 left-0 right-0 h-[1px] bg-dreamy-gold"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* MOBILE MENU TOGGLE BUTTON */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden z-[70] text-dreamy-earth p-2"
          aria-label="Toggle Menu"
        >
          {isMobileMenuOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </button>

        {/* MOBILE OVERLAY MENU */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              // This is the background that appears when you click the menu
              className="absolute top-0 left-0 w-full bg-dreamy-cream/95 backdrop-blur-xl shadow-2xl rounded-[30px] p-10 pt-28 flex flex-col items-center gap-8 z-[60] md:hidden"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="font-serif text-3xl text-dreamy-earth italic"
                >
                  {link.name}
                </Link>
              ))}
              <div className="w-12 h-[1px] bg-dreamy-gold/40 mt-4" />
              <p className="text-[9px] tracking-[0.4em] uppercase text-dreamy-earth/40">
                Sri Lanka
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </nav>
  );
};

export default Navbar;
